//any type checked blueice
import { AcademicPeriodType, IAcademicPeriod } from 'app/main/academic-period-tree';
import { GradingRubric } from 'app/main/grading-rubric/grading-rubric.model';
import { SalaryConstants } from 'app/main/salary-constants/salary-constants.model';
import { PaymentSettings } from 'app/main/schools/school-list/school-form/payment-setting/payment-setting.model';
import { AttachmentInfo } from 'app/main/shared/attachments/user-attachment.model';
import { CurrencyType } from 'app/main/shared/enum/currencyType.enum';
import { SpeechLanguageVoice } from 'app/main/shared/model/text2speech-response.model';
import { Address } from '../../shared/model/address.model';
import { Language, TranslatableString } from '../../shared/model/translateble-string.model';
import { BBBServerSetting } from './bbb-server-setting.model';
import { ContactInfo } from './contact-info.model';
import { MailServer } from './mail-server.model';
import { SchoolConstant } from './school-constant.model';
import { SmsServer } from './sms-server.model';

export const NotificationMessageType = {
    EMAIL: 'EMAIL',
    SMS: 'SMS',
    VOICE: 'VOICE',
} as const;
export type NotificationMessageType = (typeof NotificationMessageType)[keyof typeof NotificationMessageType];
export const TimeFormatType = {
    HOUR24: 'HOUR24',
    HOUR12: 'HOUR12',
} as const;
export type TimeFormatType = (typeof TimeFormatType)[keyof typeof TimeFormatType];

export const NameOrder = {
    FIRST_LAST: 'FIRST_LAST',
    LAST_FIRST: 'LAST_FIRST',
} as const;

export type NameOrder = (typeof NameOrder)[keyof typeof NameOrder];

export interface ISchool {
    id?: string;
    name?: string;
    shortName?: string;
    logo?: string;
    address?: Address;
    contactInfo?: ContactInfo;
    translateName?: TranslatableString[];
    active?: boolean;
    deletionConfirmed?: boolean;
    mailServer?: MailServer;
    smsServer?: SmsServer;
    timeZone?: string;
    phoneMask?: string;
    currency?: CurrencyType;
    language?: Language;
    salaryConstants?: SalaryConstants[];
    constants?: SchoolConstant[];
    bbbServerSetting?: BBBServerSetting;
    paymentSettings?: PaymentSettings;
    isBBBServerEnabled?: boolean;
    examDomainName?: string;
    tenantId?: string;
    gradeSettings?: GradeSettings;
    bbbServers?: MeetingServer[];
    timeFormat?: TimeFormatType;
    schoolSettings?: SchoolSettings;
    roleMessageSettings?: RoleMessageSettings[];
    dataLanguage?: string;
    activeAcademicYear?: IAcademicPeriod;
    currencyFormat?: string;
    dateFormat?: string;
    decimalFormat?: string;
    nameOrder?: NameOrder;
}

export class School implements ISchool {
    constructor(
        public id?: string,
        public name?: string,
        public shortName?: string,
        public logo?: string,
        public translateName?: TranslatableString[],
        public address?: Address,
        public contactInfo?: ContactInfo,
        public active?: boolean,
        public deletionConfirmed?: boolean,
        public mailServer?: MailServer,
        public smsServer?: SmsServer,
        public timeZone?: string,
        public currency?: CurrencyType,
        public phoneMask?: string,
        public language?: Language,
        public salaryConstants?: SalaryConstants[],
        public paymentSettings?: PaymentSettings,
        public constants?: SchoolConstant[],
        public bbbServerSetting?: BBBServerSetting,
        public isBBBServerEnabled?: boolean,
        public tenantId?: string,
        public gradeSettings?: GradeSettings,
        public bbbServers?: MeetingServer[],
        public timeFormat?: TimeFormatType,
        public schoolSettings?: SchoolSettings,
        public roleMessageSettings?: RoleMessageSettings[],
        public dataLanguage?: string,
        public activeAcademicYear?: IAcademicPeriod,
        public speechLanguageVoices?: SpeechLanguageVoice[],
        public currencyFormat?: string,
        public dateFormat?: string,
        public decimalFormat?: string,
        public twilioCredentials?: TwilioCredentials,
        public nameOrder?: NameOrder
    ) {
        this.id = id ? id : null;
        this.name = name ? name : '';
        this.shortName = shortName ? shortName : '';
        this.logo = logo ? logo : null;
        this.translateName = translateName ? translateName : [];
        this.address = address ? address : new Address();
        this.contactInfo = contactInfo ? contactInfo : new ContactInfo();
        this.active = active ? active : true;
        this.deletionConfirmed = deletionConfirmed ? deletionConfirmed : false;
        this.mailServer = mailServer ? mailServer : new MailServer();
        this.smsServer = smsServer ? smsServer : new SmsServer();
        this.timeZone = timeZone ? timeZone : '';
        this.currency = currency ? currency : null;
        this.phoneMask = phoneMask ? phoneMask : '';
        this.language = language ? language : Language.en;
        this.salaryConstants = salaryConstants ? salaryConstants : [];
        this.paymentSettings = paymentSettings ? paymentSettings : null;
        this.constants = constants ? constants : [];
        this.bbbServerSetting = bbbServerSetting ? bbbServerSetting : new BBBServerSetting();
        this.isBBBServerEnabled = isBBBServerEnabled ? isBBBServerEnabled : false;
        this.tenantId = tenantId ? tenantId : null;
        this.gradeSettings = gradeSettings ? gradeSettings : new GradeSettings();
        this.bbbServers = bbbServers ? bbbServers : [];
        this.timeFormat = timeFormat ? timeFormat : TimeFormatType.HOUR24;
        this.schoolSettings = schoolSettings ? schoolSettings : new SchoolSettings();
        this.roleMessageSettings = roleMessageSettings ? roleMessageSettings : [];
        this.dataLanguage = this.dataLanguage ?? 'en';
        this.activeAcademicYear = activeAcademicYear ?? null;
        this.speechLanguageVoices = speechLanguageVoices ?? [];
        this.currencyFormat = currencyFormat ?? null;
        this.dateFormat = dateFormat ?? null;
        this.decimalFormat = dateFormat ?? null;
        this.twilioCredentials = twilioCredentials ?? null;
        this.nameOrder = nameOrder ?? NameOrder.FIRST_LAST;
    }
}

export class MeetingServer {
    constructor(
        public url?: string,
        public secret?: string,
        public active?: boolean,
        public weight?: number,
        public id?: string,
        public failOver?: boolean,
        public proxy?: boolean,
        public whiteboardUrl?: string
    ) {
        this.url = url ? url : null;
        this.secret = secret ? secret : null;
        this.active = active ? active : false;
        this.weight = weight ? weight : 1;
        this.id = id ? id : null;
        this.failOver = failOver ? failOver : false;
        this.proxy = proxy ? proxy : false;
        this.whiteboardUrl = whiteboardUrl ?? null;
    }
}

export class GradeSettings {
    constructor(
        public enableGrading?: boolean,
        public gradeCategoriesTemplateId?: string,
        public gradeCategoryId?: string,
        public periodType?: AcademicPeriodType,
        public finalGradeTemplateId?: string,
        public gradingSchemeId?: string,
        public enableCreditSystem?: boolean,
        public gpaScaleLength?: number,
        public maxPoint?: number,
        public courseDuration?: AcademicPeriodType,
        public attendanceGradeSetting?: AttendanceGradeSetting,
        public useRubric?: boolean,
        public rubricId?: string,
        public rubric?: GradingRubric,
        public enableAssessment?: boolean,
        public enableStandartBasedGrading?: boolean
    ) {
        this.enableGrading = enableGrading ? enableGrading : false;
        this.gradeCategoriesTemplateId = gradeCategoriesTemplateId ? gradeCategoriesTemplateId : null;
        this.gradeCategoryId = gradeCategoryId ? gradeCategoryId : null;
        this.periodType = periodType ? periodType : null;
        this.finalGradeTemplateId = finalGradeTemplateId ? finalGradeTemplateId : null;
        this.gradingSchemeId = gradingSchemeId ? gradingSchemeId : null;
        this.enableCreditSystem = enableCreditSystem ? enableCreditSystem : false;
        this.gpaScaleLength = gpaScaleLength ? gpaScaleLength : 2;
        this.maxPoint = maxPoint ?? null;
        this.courseDuration = courseDuration ?? null;
        this.courseDuration = courseDuration ?? null;
        this.attendanceGradeSetting = attendanceGradeSetting ? attendanceGradeSetting : new AttendanceGradeSetting();
        this.useRubric = useRubric ?? false;
        this.rubricId = rubricId ?? null;
        this.rubric = rubric ?? null;
        this.enableAssessment = enableAssessment ?? false;
        this.enableStandartBasedGrading = enableStandartBasedGrading ?? false;
    }
}

export class AttendanceGradeSetting {
    constructor(
        public enabled?: boolean,
        public weightPresent?: number,
        public weightTardy?: number,
        public weightPermitted?: number,
        public weightExcused?: number,
        public weightAbsent?: number
    ) {
        this.enabled = this.enabled ? this.enabled : false;
        this.weightPresent = this.weightPresent ? this.weightPresent : 0;
        this.weightTardy = this.weightTardy ? this.weightTardy : 0;
        this.weightPermitted = this.weightPermitted ? this.weightPermitted : 0;
        this.weightExcused = this.weightExcused ? this.weightExcused : 0;
        this.weightAbsent = this.weightAbsent ? this.weightAbsent : 0;
    }
}

export class SchoolSettings {
    constructor(
        public cardBackgroundImages?: AttachmentInfo[],
        public considerStudentEnroll?: boolean,
        public considerOrder?: boolean,
        public considerEmployeeContract?: boolean,
        public eventTimeSlotMin?: string,
        public eventTimeSlotMax?: string,
        public businessHourTimeStart?: string,
        public businessHourTimeEnd?: string,
        public enableDailyAttendance?: boolean,
        public enableIncidentPoint?: boolean,
        public maxPoint?: number,
        public criticalPoint?: number,
        public allowAttendanceExcuseToStudent?: boolean,
        public toolbarHeight?: number,
        public hideSchoolName?: boolean,
        public academicYearPositionLeft?: string,
        public academicYearPositionBottom?: string,
        public logoWidth?: string,
        public theme?: string,
        public enabledStudentGroups?: boolean,
        public enabledStudentDiscounts?: boolean,
        public enabledStudentSimpleMedical?: boolean,
        public enabledStudentAchievement?: boolean,
        public enabledStudentAgreement?: boolean,
        public enabledStudentIdCard?: boolean,
        public enabledStudentSibling?: boolean,
        public enabledEmployeeAgreement?: boolean,
        public enabledEmployeeIdCard?: boolean,
        public enabledEmployeeCertificates?: boolean,
        public enabledEmployeeContracts?: boolean,
        public enabledEmployeeEducation?: boolean,
        public enabledEmployeeExperience?: boolean,
        public enabledEmployeeAttestation?: boolean,
        public dailyAttendanceAbsentLimit?: number,
        public courseAttendanceNotificationTime?: string,
        public notificationTypes?: NotificationMessageType[],
        public enableChat?: boolean
    ) {
        this.dailyAttendanceAbsentLimit = dailyAttendanceAbsentLimit ?? 0;
        this.courseAttendanceNotificationTime = courseAttendanceNotificationTime ?? null;
        this.notificationTypes = notificationTypes ?? [];
        this.cardBackgroundImages = cardBackgroundImages ? cardBackgroundImages : [];
        this.considerStudentEnroll = considerStudentEnroll ? considerStudentEnroll : false;
        this.considerOrder = considerOrder ? considerOrder : false;
        this.considerEmployeeContract = considerEmployeeContract ? considerEmployeeContract : false;
        this.eventTimeSlotMin = eventTimeSlotMin ?? '00:00';
        this.eventTimeSlotMax = eventTimeSlotMax ?? '23:00';
        this.businessHourTimeStart = businessHourTimeStart ?? '06:00';
        this.businessHourTimeEnd = businessHourTimeEnd ?? '20:00';
        this.enableDailyAttendance = enableDailyAttendance ? enableDailyAttendance : false;
        this.enableIncidentPoint = enableIncidentPoint ? enableIncidentPoint : false;
        this.maxPoint = maxPoint ? maxPoint : 0;
        this.criticalPoint = criticalPoint ? criticalPoint : 0;
        this.allowAttendanceExcuseToStudent = allowAttendanceExcuseToStudent ? allowAttendanceExcuseToStudent : false;
        this.toolbarHeight = this.toolbarHeight ?? 74;
        this.hideSchoolName = this.hideSchoolName ?? false;
        this.academicYearPositionLeft = this.academicYearPositionLeft ?? '80px';
        this.academicYearPositionBottom = this.academicYearPositionBottom ?? '20px';
        this.logoWidth = this.logoWidth ?? '200px';
        this.theme = this.theme ?? 'default-theme';
        this.enabledStudentGroups = enabledStudentGroups ?? false;
        this.enabledStudentDiscounts = enabledStudentDiscounts ?? false;
        this.enabledStudentSimpleMedical = enabledStudentSimpleMedical ?? false;
        this.enabledStudentAchievement = enabledStudentAchievement ?? false;
        this.enabledStudentAgreement = enabledStudentAgreement ?? false;
        this.enabledStudentIdCard = enabledStudentIdCard ?? false;
        this.enabledEmployeeAgreement = enabledEmployeeAgreement ?? false;
        this.enabledEmployeeIdCard = enabledEmployeeIdCard ?? false;
        this.enabledStudentSibling = enabledStudentSibling ?? false;
        this.enabledEmployeeCertificates = enabledEmployeeCertificates ?? false;
        this.enabledEmployeeContracts = enabledEmployeeContracts ?? false;
        this.enabledEmployeeEducation = enabledEmployeeEducation ?? false;
        this.enabledEmployeeExperience = enabledEmployeeExperience ?? false;
        this.enabledEmployeeAttestation = enabledEmployeeAttestation ?? false;
        this.enableChat = enableChat ?? true;
    }
}

export class RoleMessageSettings {
    constructor(
        public roleId?: string,
        public messageSettings?: MessageSettings
    ) {
        this.roleId = roleId ? roleId : null;
        this.messageSettings = messageSettings ? messageSettings : new MessageSettings();
    }
}

export class MessageSettings {
    constructor(
        public courseMembers?: boolean,
        public classMembers?: boolean,
        public teachers?: boolean,
        public schoolAdministrations?: boolean,
        public tenantAdmin?: boolean
    ) {
        this.courseMembers = courseMembers ? courseMembers : false;
        this.classMembers = classMembers ? classMembers : false;
        this.teachers = teachers ? teachers : false;
        this.schoolAdministrations = schoolAdministrations ? schoolAdministrations : false;
        this.tenantAdmin = tenantAdmin ? tenantAdmin : false;
    }
}

export class TwilioCredentials {
    constructor(
        public accountSid?: string,
        public authToken?: string,
        public phoneNumber?: string,
        public messageServiceSid?: string,
        public enabled?: boolean
    ) {}
}
